import React from "react";
import { Link } from "react-router-dom";
import { 
  Award, Heart, BarChart3, Lightbulb, MapPin,
  Building2, Briefcase, Store, User, Wrench, Building, Users,
  Target, Rocket, Globe, Zap
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEOHead from "../components/SEOHead";

const AProposPage = () => {
  const { t, language } = useLanguage();

  const values = [
    { icon: <Award className="w-6 h-6" />, title: t('aboutPage.excellence'), desc: t('aboutPage.excellenceDesc') },
    { icon: <Heart className="w-6 h-6" />, title: t('aboutPage.proximity'), desc: t('aboutPage.proximityDesc') },
    { icon: <BarChart3 className="w-6 h-6" />, title: t('aboutPage.results'), desc: t('aboutPage.resultsDesc') },
    { icon: <Lightbulb className="w-6 h-6" />, title: t('aboutPage.innovation'), desc: t('aboutPage.innovationDesc') },
  ];

  const visionMission = [
    { 
      icon: <Target className="w-8 h-8" />, 
      title: t('aboutPage.missionTitle'), 
      desc: t('aboutPage.missionDesc')
    },
    { 
      icon: <Rocket className="w-8 h-8" />, 
      title: t('aboutPage.visionTitle'), 
      desc: t('aboutPage.visionDesc')
    },
    { 
      icon: <Globe className="w-8 h-8" />, 
      title: t('aboutPage.philosophyTitle'), 
      desc: t('aboutPage.philosophyDesc')
    },
  ];

  const industries = language === 'fr' ? [
    "Tech & SaaS", "MedTech & Santé", "Finance & Fintech", "Retail & E-commerce",
    "Industrie & Manufacturing", "Immobilier", "Luxe & Mode", "Food & Beverage",
    "Énergie & Environnement", "Éducation & Formation", "Tourisme & Hospitality", "Services B2B"
  ] : [
    "Tech & SaaS", "MedTech & Health", "Finance & Fintech", "Retail & E-commerce",
    "Industry & Manufacturing", "Real Estate", "Luxury & Fashion", "Food & Beverage",
    "Energy & Environment", "Education & Training", "Tourism & Hospitality", "B2B Services"
  ];

  const locations = [
    { city: "Lyon", country: "France", hq: true },
    { city: "Paris", country: "France", hq: false },
    { city: language === 'fr' ? "Genève" : "Geneva", country: language === 'fr' ? "Suisse" : "Switzerland", hq: false },
    { city: language === 'fr' ? "Bruxelles" : "Brussels", country: language === 'fr' ? "Belgique" : "Belgium", hq: false },
    { city: "Nashville", country: language === 'fr' ? "USA" : "USA", hq: false },
    { city: "Kigali", country: "Rwanda", hq: false },
    { city: "Bangui", country: language === 'fr' ? "Centrafrique" : "Central African Republic", hq: false },
    { city: "Cotonou", country: language === 'fr' ? "Bénin" : "Benin", hq: false },
  ];

  const clients = [
    { icon: <Building2 className="w-8 h-8" />, name: "Startups", desc: language === 'fr' ? "Startups innovantes en phase de croissance" : "Innovative startups in growth phase" },
    { icon: <Briefcase className="w-8 h-8" />, name: language === 'fr' ? "PME" : "SMEs", desc: language === 'fr' ? "PME de 1 à 20M€ de chiffre d'affaires" : "SMEs with €1-20M revenue" },
    { icon: <Store className="w-8 h-8" />, name: language === 'fr' ? "Commerçants" : "Retailers", desc: language === 'fr' ? "Commerces locaux et e-commerce" : "Local shops and e-commerce" },
    { icon: <User className="w-8 h-8" />, name: "Freelancers", desc: language === 'fr' ? "Indépendants & consultants" : "Independents & consultants" },
    { icon: <Wrench className="w-8 h-8" />, name: language === 'fr' ? "Artisans" : "Craftsmen", desc: language === 'fr' ? "Artisans et TPE" : "Craftsmen and small businesses" },
    { icon: <Building className="w-8 h-8" />, name: language === 'fr' ? "Grands groupes" : "Large groups", desc: language === 'fr' ? "Grandes entreprises et multinationales" : "Large companies and multinationals" },
    { icon: <Users className="w-8 h-8" />, name: language === 'fr' ? "Cabinets de conseil" : "Consulting firms", desc: language === 'fr' ? "Cabinets B2B et services professionnels" : "B2B firms and professional services" },
  ];

  return (
    <div className="pt-20">
      <SEOHead 
        title={language === 'fr' 
          ? "À Propos | ImpaKt Studio - Agence Internationale de Branding" 
          : "About | ImpaKt Studio - International Branding Agency"
        }
        description={language === 'fr'
          ? "Découvrez ImpaKt Studio, groupe international de branding et stratégie digitale. 15+ ans d'expertise, 8 bureaux sur 3 continents."
          : "Discover ImpaKt Studio, international branding and digital strategy group. 15+ years of expertise, 8 offices on 3 continents."
        }
        path="/a-propos"
      />
      
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-cyan-500 to-cyan-600" data-testid="apropos-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-cyan-100 font-medium text-sm uppercase tracking-wider">{t('aboutPage.heroSubtitle')}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            ImpaKt Studio
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {t('aboutPage.heroDesc')}
          </p>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white" data-testid="story-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80"
                alt={language === 'fr' ? "Équipe ImpaKt Studio" : "ImpaKt Studio Team"}
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="flex items-center justify-center gap-6 mt-8">
                <div className="bg-cyan-50 rounded-2xl px-6 py-4 text-center">
                  <div className="text-3xl font-bold text-cyan-600">15+</div>
                  <div className="text-gray-600 text-sm">{t('aboutPage.yearsLabel')}</div>
                </div>
                <div className="bg-gray-50 rounded-2xl px-6 py-4 text-center">
                  <div className="text-3xl font-bold text-gray-800">150+</div>
                  <div className="text-gray-600 text-sm">{t('aboutPage.projectsLabel')}</div>
                </div>
                <div className="bg-cyan-50 rounded-2xl px-6 py-4 text-center">
                  <div className="text-3xl font-bold text-cyan-600">3</div>
                  <div className="text-gray-600 text-sm">{language === 'fr' ? 'Continents' : 'Continents'}</div>
                </div>
              </div>
            </div>
            
            <div>
              <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">{t('aboutPage.whoWeAre')}</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-6">
                {t('aboutPage.whyChoose')}
              </h2>
              <div className="prose prose-lg text-gray-600 space-y-4">
                <p>
                  <strong className="text-gray-900">ImpaKt Studio</strong> {t('aboutPage.aboutDesc1')}
                </p>
                <p>
                  {t('aboutPage.aboutDesc2')} <strong className="text-cyan-600">{t('aboutPage.continents')}</strong> {t('aboutPage.and')} <strong className="text-cyan-600">{t('aboutPage.offices')}</strong> {t('aboutPage.aboutDesc3')}
                </p>
                <p>
                  {t('aboutPage.aboutDesc4')} <strong className="text-cyan-600">{t('aboutPage.yearsExperience')}</strong> {t('aboutPage.aboutDesc5')}
                </p>
                <p className="italic border-l-4 border-cyan-500 pl-4 bg-cyan-50 py-3 rounded-r-lg">
                  "{t('aboutPage.ambitionQuote')}"
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6 mt-10">
                {values.map((value) => (
                  <div key={value.title} className="flex gap-3">
                    <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center text-cyan-600 flex-shrink-0">
                      {value.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{value.title}</h4>
                      <p className="text-sm text-gray-600">{value.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Philosophie */}
      <section className="py-20 bg-gray-900" data-testid="vision-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-semibold text-sm uppercase tracking-wider">{t('aboutPage.whatDrivesUs')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-white mt-4">
              {t('aboutPage.visionMissionTitle')}
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {visionMission.map((item, index) => (
              <div
                key={item.title}
                className="bg-gray-800 rounded-2xl p-8 border border-gray-700 hover:border-cyan-500 transition-all duration-300 group"
                data-testid={`vision-card-${index}`}
              >
                <div className="w-16 h-16 rounded-2xl bg-cyan-500/20 flex items-center justify-center text-cyan-400 mb-6 group-hover:bg-cyan-500 group-hover:text-white transition-all duration-300">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{item.title}</h3>
                <p className="text-gray-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries / Multi-secteur */}
      <section className="py-20 bg-white" data-testid="industries-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">{t('aboutPage.multiSectorTitle')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              {t('aboutPage.allSectorsTitle')}
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              {t('aboutPage.allSectorsDesc')}
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {industries.map((industry, index) => (
              <span
                key={index}
                className="px-5 py-2.5 bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 rounded-full font-medium hover:from-cyan-50 hover:to-cyan-100 hover:text-cyan-700 transition-all duration-300 cursor-default border border-gray-200 hover:border-cyan-300"
              >
                {industry}
              </span>
            ))}
            <span className="px-5 py-2.5 bg-cyan-500 text-white rounded-full font-semibold">
              {t('aboutPage.andMore')}
            </span>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-500 italic">
              "{t('aboutPage.industryQuote')}"
            </p>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-20 bg-gray-50" data-testid="locations-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">{t('aboutPage.internationalPresence')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              {t('aboutPage.officesTitle')}
            </h2>
            <p className="text-gray-600">
              {t('aboutPage.officesDesc')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {locations.map((location) => (
              <div
                key={location.city}
                className={`bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-300 border ${
                  location.hq ? 'border-cyan-500 ring-2 ring-cyan-100' : 'border-gray-100'
                }`}
                data-testid={`location-${location.city.toLowerCase()}`}
              >
                {location.hq && (
                  <span className="inline-block px-2 py-1 bg-cyan-100 text-cyan-700 text-xs font-semibold rounded-full mb-3">
                    {t('aboutPage.headquarters')}
                  </span>
                )}
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-5 h-5 text-cyan-500" />
                  <h3 className="font-bold text-gray-900">{location.city}</h3>
                </div>
                <p className="text-gray-600 text-sm">{location.country}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients */}
      <section className="py-20 bg-white" data-testid="clients-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-cyan-600 font-semibold text-sm uppercase tracking-wider">{t('aboutPage.ourClients')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4 mb-4">
              {t('aboutPage.whoWeWorkWith')}
            </h2>
            <p className="text-gray-600">
              {t('aboutPage.clientsDesc')}
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {clients.map((client) => (
              <div
                key={client.name}
                className="bg-gray-50 rounded-xl p-6 text-center hover:bg-cyan-50 transition-colors duration-300"
                data-testid={`client-${client.name.toLowerCase()}`}
              >
                <div className="w-12 h-12 mx-auto rounded-xl bg-white shadow-sm flex items-center justify-center text-cyan-600 mb-4">
                  {client.icon}
                </div>
                <h3 className="font-semibold text-gray-900 text-sm mb-1">{client.name}</h3>
                <p className="text-gray-500 text-xs">{client.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-cyan-500 to-cyan-600" data-testid="apropos-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {t('aboutPage.workTogether')}
          </h2>
          <p className="text-white/80 text-lg mb-10">
            {t('aboutPage.workTogetherDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-white text-cyan-600 font-semibold rounded-full hover:bg-gray-100 transition-all duration-300 shadow-lg"
              data-testid="apropos-cta-contact"
            >
              {t('common.contactUs')}
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300"
              data-testid="apropos-cta-services"
            >
              {t('common.ourServices')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AProposPage;
